<template>
  <div class="viq-section-stream">
    <h3 class="viq-section-stream__title">Generating Report</h3>

    <div class="viq-section-stream__sections">
      <div
        v-for="(section, i) in sections"
        :key="i"
        class="viq-section-stream__section"
        :class="'viq-section-stream__section--' + section.status"
      >
        <div class="viq-section-stream__section-header">
          <span class="viq-section-stream__section-status">
            <svg v-if="section.status === 'complete'" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span v-else-if="section.status === 'streaming'" class="viq-section-stream__spinner" />
            <span v-else class="viq-section-stream__pending" />
          </span>
          <h4 class="viq-section-stream__section-title">{{ section.title }}</h4>
        </div>

        <div
          v-if="section.content"
          class="viq-section-stream__section-content"
          v-html="renderContent(section.content)"
        />
      </div>
    </div>

    <div v-if="isStreaming" class="viq-section-stream__progress">
      <el-progress
        :percentage="progressPercent"
        :stroke-width="4"
        color="#2563eb"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DOMPurify from "dompurify";

export default {
  name: "ViqReportSectionStream",
  props: {
    sections: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState("Viq", ["isStreaming"]),
    progressPercent() {
      if (!this.sections.length) return 0;
      const complete = this.sections.filter((s) => s.status === "complete").length;
      const streaming = this.sections.filter((s) => s.status === "streaming").length;
      return Math.round(((complete + streaming * 0.5) / this.sections.length) * 100);
    },
  },
  methods: {
    renderContent(text) {
      if (!text) return "";
      // Simple markdown to HTML
      let html = text;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
      html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
      html = html.replace(/^[*-] (.+)$/gm, "<li>$1</li>");
      html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";
      html = html.replace(/<p><\/p>/g, "");
      html = html.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(html);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-section-stream {
  padding: $viq-space-lg 0;

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0 0 $viq-space-lg;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: $viq-space-md;
  }

  &__section {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    transition: border-color $viq-transition-base;

    &--streaming {
      border-color: $viq-accent;
    }

    &--complete {
      border-color: $viq-success;
    }

    &-header {
      display: flex;
      align-items: center;
      gap: $viq-space-sm;
      margin-bottom: $viq-space-sm;
    }

    &-status {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $viq-success;
    }

    &-title {
      font-size: $viq-font-md;
      font-weight: 700;
      color: $viq-text-primary;
      margin: 0;
    }

    &-content {
      font-size: $viq-font-base;
      line-height: 1.7;
      color: $viq-text-primary;
      padding-left: 28px;

      ::v-deep {
        p { margin: 0 0 $viq-space-sm; }
        h2, h3 { margin: $viq-space-md 0 $viq-space-sm; font-weight: 700; }
        ul { padding-left: $viq-space-lg; margin: $viq-space-sm 0; }
        li { margin-bottom: $viq-space-xs; }
        strong { font-weight: 700; }
      }
    }
  }

  &__spinner {
    width: 14px;
    height: 14px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__pending {
    width: 10px;
    height: 10px;
    background: $viq-border;
    border-radius: 50%;
  }

  &__progress {
    margin-top: $viq-space-lg;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
